import type { GridFilterCellProps } from "@tql/ui-components-react/kendo/grid";
import type { InputChangeEvent } from "@tql/ui-components-react/kendo/inputs";
import { Input } from "@tql/ui-components-react/kendo/inputs";

import type { DateSearchTimeZone } from "~/shared/utils/date-search.ts";
import { dateMatchesSearch } from "~/shared/utils/date-search.ts";

/**
 * Builds a Kendo filter operator that matches a Date field value against the
 * user-entered text using the shared date search rules (M/D, MM/DD, M/D/YY,
 * M/D/YYYY, ...). `timeZone` must match how the column renders its date so the
 * displayed date is the searched date. Allows the column to bind to a real
 * Date value for correct chronological sorting.
 */
export const createDateContainsOperator =
	(timeZone: DateSearchTimeZone) =>
	(fieldValue: Date | string | null | undefined, filterValue: string) =>
		dateMatchesSearch(fieldValue, filterValue, timeZone);

const utcDateContainsOperator = createDateContainsOperator("utc");
const localDateContainsOperator = createDateContainsOperator("local");

const DateTextFilterCellBase = ({
	operator,
	...props
}: GridFilterCellProps & {
	operator: (
		fieldValue: Date | string | null | undefined,
		filterValue: string,
	) => boolean;
}) => {
	const value = (props.value ?? "") as string;

	const handleChange = (event: InputChangeEvent) => {
		const next = String(event.value ?? "");
		props.onChange({
			value: next,
			operator: next === "" ? "" : operator,
			syntheticEvent: event.syntheticEvent,
		});
	};

	return (
		// Mirror the wrapper structure produced by Kendo's default
		// `GridFilterCell` (`<div class="k-filtercell"><div class="k-filtercell-wrapper">…</div></div>`).
		// Kendo only emits these classes from its own default filter cell; when
		// a custom `filterCell` is supplied they are omitted. We restore them so
		// that the `TraxGrid` search-icon injector and the grid filter tab
		// navigation hook (both of which target `.k-filtercell`) continue to
		// pick up this column.
		<div className={"k-filtercell"}>
			<div className={"k-filtercell-wrapper"}>
				<Input aria-label={"Filter"} onChange={handleChange} value={value} />
			</div>
		</div>
	);
};

/** For columns rendered in UTC (e.g. `DateCell`, `LoadDateCell`). */
export const DateTextFilterCell = (props: GridFilterCellProps) => (
	<DateTextFilterCellBase {...props} operator={utcDateContainsOperator} />
);

/** For columns rendered in the user's local time zone. */
export const LocalDateTextFilterCell = (props: GridFilterCellProps) => (
	<DateTextFilterCellBase {...props} operator={localDateContainsOperator} />
);
